import React, { useState, useEffect } from 'react';
import { 
  Receipt, Search, CheckCircle2, XCircle, Clock, 
  DollarSign, Eye, FileText, Plane, Heart, Monitor,
  GraduationCap, Utensils, Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { db } from '../../../lib/db';
import { useReimbursements } from '../../../hooks/useDatabase';
import { formatCurrency, getPayrollStatusColor, formatPayrollStatus, getReimbursementCategoryInfo } from '../../../lib/payrollCalculations';
import { formatDate, formatTimeAgo } from '../../../lib/dateUtils';
import { useAuthStore } from '../../../store/authStore';
import { toast } from 'sonner';

const categoryIcons = {
  travel: Plane,
  medical: Heart,
  equipment: Monitor,
  training: GraduationCap,
  meals: Utensils,
  other: FileText
};

export default function ReimbursementManagement() {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [reimbursements, setReimbursements] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedReimbursement, setSelectedReimbursement] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const { approveReimbursement, rejectReimbursement } = useReimbursements();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [reimbursementsData, usersData] = await Promise.all([
        db.reimbursements.toArray(),
        db.users.toArray()
      ]);
      setReimbursements(reimbursementsData);
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredReimbursements = reimbursements.filter(r => {
    const employee = users.find(u => u.id === r.userId);
    const matchesSearch = 
      employee?.profile?.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      employee?.profile?.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || r.category === categoryFilter;
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const pendingReimbursements = reimbursements.filter(r => r.status === 'pending');
  const approvedReimbursements = reimbursements.filter(r => r.status === 'approved');
  const totalPending = pendingReimbursements.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  const totalApproved = approvedReimbursements.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);

  const handleApprove = async (id) => {
    try {
      await approveReimbursement(id, user.id);
      toast.success('Reimbursement approved');
      setShowDetailModal(false);
      loadData();
    } catch (error) {
      toast.error('Failed to approve reimbursement');
    }
  };

  const handleReject = async (id) => {
    if (!rejectionReason) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      await rejectReimbursement(id, rejectionReason);
      toast.success('Reimbursement rejected');
      setShowDetailModal(false);
      setRejectionReason('');
      loadData();
    } catch (error) {
      toast.error('Failed to reject reimbursement');
    }
  };

  const getEmployeeName = (userId) => {
    const employee = users.find(u => u.id === userId);
    return employee ? `${employee.profile?.firstName} ${employee.profile?.lastName}` : 'Unknown';
  };

  const getEmployeeDetails = (userId) => {
    return users.find(u => u.id === userId);
  };

  const viewDetails = (reimbursement) => {
    setSelectedReimbursement(reimbursement);
    setShowDetailModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reimbursements</h1>
          <p className="text-gray-500 mt-1">Review and approve employee expense claims</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Claims</p>
                <p className="text-2xl font-bold text-gray-900">{reimbursements.length}</p>
              </div>
              <Receipt className="w-8 h-8 text-teal-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Review</p>
                <p className="text-2xl font-bold text-amber-600">{pendingReimbursements.length}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Amount</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalPending)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-amber-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Approved Total</p>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(totalApproved)}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {Object.keys(categoryIcons).map(category => {
          const info = getReimbursementCategoryInfo(category);
          const Icon = categoryIcons[category];
          const count = reimbursements.filter(r => r.category === category).length;
          const amount = reimbursements.filter(r => r.category === category).reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
          
          return (
            <Card 
              key={category} 
              className={`border-0 shadow-sm cursor-pointer transition-all ${categoryFilter === category ? 'ring-2 ring-teal-500' : ''}`}
              onClick={() => setCategoryFilter(categoryFilter === category ? 'all' : category)}
            >
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${info.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{info.label}</p>
                    <p className="font-semibold text-gray-900">{count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by employee or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Reimbursements Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Employee</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredReimbursements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No reimbursements found
                  </TableCell>
                </TableRow>
              ) : (
                filteredReimbursements.map(reimbursement => {
                  const categoryInfo = getReimbursementCategoryInfo(reimbursement.category);
                  const Icon = categoryIcons[reimbursement.category] || FileText;
                  
                  return (
                    <TableRow key={reimbursement.id} className="hover:bg-gray-50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-violet-400 to-purple-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
                            {getEmployeeName(reimbursement.userId).split(' ').map(n => n[0]).join('')}
                          </div>
                          <span className="font-medium">{getEmployeeName(reimbursement.userId)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={categoryInfo.color}>
                          <Icon className="w-3 h-3 mr-1" />
                          {categoryInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-xs truncate text-gray-600">
                        {reimbursement.description || '-'}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCurrency(reimbursement.amount)}
                      </TableCell>
                      <TableCell>
                        <Badge className={getPayrollStatusColor(reimbursement.status)}>
                          {formatPayrollStatus(reimbursement.status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-gray-500 text-sm">
                        {formatTimeAgo(reimbursement.submittedAt)}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex justify-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => viewDetails(reimbursement)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {reimbursement.status === 'pending' && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                onClick={() => handleApprove(reimbursement.id)}
                              >
                                <CheckCircle2 className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => viewDetails(reimbursement)}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Reimbursement Details</DialogTitle>
            <DialogDescription>
              Review the expense claim details
            </DialogDescription>
          </DialogHeader>

          {selectedReimbursement && (
            <div className="space-y-4">
              {/* Employee Info */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-gradient-to-br from-violet-400 to-purple-500 rounded-full flex items-center justify-center text-white font-medium">
                  {getEmployeeName(selectedReimbursement.userId).split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{getEmployeeName(selectedReimbursement.userId)}</p>
                  <p className="text-sm text-gray-500">
                    {getEmployeeDetails(selectedReimbursement.userId)?.profile?.department} • 
                    {getEmployeeDetails(selectedReimbursement.userId)?.profile?.position}
                  </p>
                </div>
              </div>

              {/* Claim Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Category</p>
                  <Badge className={getReimbursementCategoryInfo(selectedReimbursement.category).color}>
                    {getReimbursementCategoryInfo(selectedReimbursement.category).label}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Amount</p>
                  <p className="text-xl font-bold text-gray-900">{formatCurrency(selectedReimbursement.amount)}</p>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-1">Description</p>
                <p className="text-gray-900">{selectedReimbursement.description || 'No description provided'}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Submitted</p>
                  <p className="font-medium">{formatDate(selectedReimbursement.submittedAt)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Status</p>
                  <Badge className={getPayrollStatusColor(selectedReimbursement.status)}>
                    {formatPayrollStatus(selectedReimbursement.status)}
                  </Badge>
                </div>
              </div>

              {selectedReimbursement.status === 'rejected' && selectedReimbursement.rejectionReason && (
                <div className="p-3 bg-red-50 rounded-lg">
                  <p className="text-sm font-medium text-red-700">Rejection Reason:</p>
                  <p className="text-sm text-red-600">{selectedReimbursement.rejectionReason}</p>
                </div>
              )}

              {selectedReimbursement.status === 'pending' && (
                <div>
                  <Label htmlFor="rejectionReason">Rejection Reason (if rejecting)</Label>
                  <Textarea
                    id="rejectionReason"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Enter reason for rejection..."
                    className="mt-1"
                    rows={2}
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>Close</Button>
            {selectedReimbursement?.status === 'pending' && (
              <>
                <Button 
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => handleReject(selectedReimbursement.id)}
                >
                  <XCircle className="w-4 h-4 mr-2" /> Reject
                </Button>
                <Button 
                  onClick={() => handleApprove(selectedReimbursement.id)}
                  className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Approve
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
